/**
 * i18n/he.ts — every user-visible string of LabTop Finance.
 *
 * ARCHITECTURE §10: "Copy is templated: i18n/he.ts holds every string (no literals in
 * components); card masks, names, counts, dates and plan/credit figures come from the API.
 * Where a value is unavailable (credits used, plan) the component renders the mockup's label
 * with 'לא זמין', never a demo number."
 *
 * Wording is the approved mockup's (`mockups/business.html`) and spec-business.md §4/§5/§7.
 */

import type {
  AccountType,
  AlertType,
  CallStatus,
  CallType,
  ConnectionStatus,
  DocType,
  ErrorCode,
  ExpenseCategory,
  InvoiceStatus,
  PaymentChannel,
  PaymentMethod,
  ProviderId,
  ServiceLine,
  SubscriptionStatus,
  TxScope,
  VatPeriodStatus,
} from '../../../api-types.ts';

export const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
export const HE_MONTHS_SHORT = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];
export const HE_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/** The 7 operational expense categories, in the spec's display order (§2.1). */
/**
 * The 7 operating categories. `icon` is the app's own `Icon.tsx` name; `glyph` is the drawn
 * `CategoryIcon` glyph of REDESIGN §7b, which is what every category, expense and alert row shows.
 */
export const CATEGORY: Readonly<Record<ExpenseCategory, { label: string; icon: string; glyph: string }>> = {
  EQUIPMENT: { label: 'ציוד וחלפים', icon: 'server', glyph: 'tv' },
  SOFTWARE: { label: 'תוכנה ושירותים', icon: 'monitor', glyph: 'bolt' },
  TELECOM: { label: 'תקשורת', icon: 'globe', glyph: 'phone' },
  VEHICLE: { label: 'רכב ודלק', icon: 'car', glyph: 'fuel' },
  MARKETING: { label: 'שיווק', icon: 'zap', glyph: 'sparkle' },
  BOOKKEEPING: { label: 'הנה"ח ועמלות', icon: 'percent', glyph: 'percent' },
  CONTRACTORS: { label: 'שכר/קבלנים', icon: 'users', glyph: 'briefcase' },
};

/** Non-operational scopes that ride in the bank cash flow but never in "expenses" (§2.1). */
export const SCOPE: Readonly<Record<TxScope, string>> = {
  expense: 'הוצאה',
  receipt: 'תקבול',
  tax: 'מסים',
  card_settlement: 'חיוב כרטיס',
  owner_draw: 'משיכת בעלים',
  transfer: 'העברה פנימית',
  ignore: 'לא נספר',
};

/**
 * Provider NAMES only. The mark itself is drawn by `ProviderMark` from `shared/ui` (REDESIGN §7a)
 * — a rounded square in the provider's colour carrying a monogram we drew — so no text letter and
 * no dingbat stands in for a logo any more.
 */
export const PROVIDER: Readonly<Record<string, { name: string }>> = {
  discount: { name: 'דיסקונט' },
  cal: { name: 'Cal' },
  max: { name: 'Max' },
  isracard: { name: 'ישראכרט' },
  leumi: { name: 'לאומי' },
  hapoalim: { name: 'הפועלים' },
  mizrahi: { name: 'מזרחי' },
  beinleumi: { name: 'הבינלאומי' },
  'one-zero': { name: 'One Zero' },
  pepper: { name: 'Pepper' },
  amex: { name: 'אמריקן אקספרס' },
  manual: { name: 'ידני' },
};

export function providerName(id: ProviderId): string {
  return PROVIDER[id]?.name ?? String(id);
}

export const ACCOUNT_TYPE: Readonly<Record<AccountType, string>> = {
  CHECKING: 'עו"ש',
  SAVINGS: 'פיקדון',
  SECURITY: 'ניירות ערך',
  LOAN: 'הלוואה',
  CARD: 'כרטיס אשראי',
};

export const CONNECTION_STATUS: Readonly<Record<ConnectionStatus, { label: string; chip: string; icon: string }>> = {
  ACTIVE: { label: 'פעיל', chip: 'good', icon: 'check' },
  COMPLETED: { label: 'משיכה חד-פעמית', chip: 'outline', icon: 'clock' },
  EXPIRED: { label: 'פג תוקף', chip: 'critical', icon: 'alertCircle' },
  REVOKED: { label: 'בוטל', chip: 'outline', icon: 'x' },
  REJECTED: { label: 'נדחה', chip: 'critical', icon: 'alertCircle' },
  FETCHING: { label: 'מתחבר…', chip: 'outline', icon: 'clock' },
};

export const DOC_TYPE: Readonly<Record<DocType, { label: string; short: string }>> = {
  proforma: { label: 'חשבונית עסקה', short: 'עסקה' },
  tax: { label: 'חשבונית מס', short: 'מס' },
  tax_receipt: { label: 'חשבונית מס-קבלה', short: 'מס-קבלה' },
  receipt: { label: 'קבלה', short: 'קבלה' },
  credit: { label: 'חשבונית זיכוי', short: 'זיכוי' },
};

export const PAYMENT_METHOD: Readonly<Record<PaymentMethod, string>> = {
  transfer: 'העברה בנקאית',
  standing_order: 'הו"ק HYP',
  card: 'כרטיס HYP',
  cash: 'מזומן',
};

export const PAYMENT_CHANNEL: Readonly<Record<PaymentChannel, string>> = {
  card: 'כרטיס',
  bank_transfer: 'העברה',
  standing_order: 'הו"ק בנק',
};

export const INVOICE_STATUS: Readonly<Record<InvoiceStatus, { chip: string; icon: string }>> = {
  paid: { chip: 'good', icon: 'check' },
  open: { chip: 'info', icon: 'circle' },
  overdue: { chip: 'critical', icon: 'alertCircle' },
  credited: { chip: 'outline', icon: 'rotate' },
};

export const VAT_STATUS: Readonly<Record<VatPeriodStatus, string>> = {
  open: 'טרם דווח',
  reported: 'דווח',
  paid: 'שולם',
};

export const SERVICE_LINE: Readonly<Record<ServiceLine, string>> = {
  subscriptions: 'מנויים',
  field: 'טכנאי בשטח',
  remote: 'תמיכה מרחוק',
};

export const CALL_STATUS: Readonly<Record<CallStatus, { label: string; chip: string; icon: string }>> = {
  open: { label: 'פתוחה', chip: 'warn', icon: 'circle' },
  in_progress: { label: 'בטיפול', chip: 'info', icon: 'clock' },
  done: { label: 'הושלמה', chip: 'good', icon: 'check' },
};

export const CALL_TYPE: Readonly<Record<CallType, { label: string; icon: string }>> = {
  remote: { label: 'מרחוק', icon: 'monitor' },
  field: { label: 'שטח', icon: 'car' },
};

export const SUBSCRIPTION_STATUS: Readonly<Record<SubscriptionStatus, { label: string; chip: string; icon: string }>> = {
  active: { label: 'פעיל', chip: 'good', icon: 'check' },
  paused: { label: 'מושהה', chip: 'warn', icon: 'pause' },
  cancelled: { label: 'בוטל', chip: 'outline', icon: 'x' },
};

/** REDESIGN §7b — the drawn glyph an alert row shows, one per alert type. */
export const ALERT_GLYPH: Readonly<Record<AlertType, string>> = {
  INVOICE_OVERDUE: 'receipt',
  VAT_DUE: 'landmark',
  TAX_ADVANCE_DUE: 'percent',
  CARD_CHARGE_SOON: 'card',
  CATEGORY_OVER_BASELINE: 'coins',
  EXPENSE_NO_INVOICE: 'alert',
  RECEIPT_UNMATCHED: 'exchange',
  CONNECTION_EXPIRED: 'transfer',
  DATA_STALE: 'arrows',
  SUBSCRIPTION_CANCELLED: 'undo',
  FORECAST_NEGATIVE: 'banknote',
};

export const ALERT_ICON: Readonly<Record<AlertType, string>> = {
  INVOICE_OVERDUE: 'file',
  VAT_DUE: 'landmark',
  TAX_ADVANCE_DUE: 'percent',
  CARD_CHARGE_SOON: 'card',
  CATEGORY_OVER_BASELINE: 'barChart',
  EXPENSE_NO_INVOICE: 'flag',
  RECEIPT_UNMATCHED: 'arrowLeftRight',
  CONNECTION_EXPIRED: 'link',
  DATA_STALE: 'clock',
  SUBSCRIPTION_CANCELLED: 'users',
  FORECAST_NEGATIVE: 'activity',
};

export const he = {
  app: {
    name: 'LabTop Finance',
    kind: 'עסקי · עוסק מורשה',
    logoMark: 'LT',
    skip: 'דילוג לתוכן',
    close: 'סגור',
    cancel: 'ביטול',
    save: 'שמור',
    apply: 'החל',
    back: 'חזרה',
    details: 'פרטים',
    clear: 'נקה סינון',
    loading: 'טוען נתונים…',
    loadingScreen: 'טוען',
    demo: 'נתוני הדגמה',
    demoTitle: 'חלק מהנתונים במסך הם נתוני הדגמה',
    notAvailable: 'לא זמין',
    total: 'סה"כ',
    of: 'מתוך',
    days: 'ימים',
    day: 'יום',
    none: '—',
    more: 'עוד',
    fullVersion: 'הקובץ ייווצר בגרסה המלאה',
    tableView: 'טבלה',
    chartView: 'תרשים',
    legend: 'מקרא',
  },

  nav: {
    main: 'ניווט ראשי',
    bottom: 'ניווט תחתון',
    sidebar: 'סרגל צד',
    home: 'ראשי',
    cashflow: 'תזרים',
    invoices: 'חשבוניות וגבייה',
    invoicesShort: 'גבייה',
    vat: 'מע"מ ומסים',
    vatShort: 'מע"מ',
    expenses: 'הוצאות וספקים',
    loans: 'הלוואות',
    ops: 'תפעול LabTop',
    alerts: 'מרכז התראות',
    settings: 'הגדרות',
    logout: 'יציאה',
    collapse: 'כווץ תפריט',
    expand: 'הרחב תפריט',
    more: 'עוד',
    moreSheet: 'עוד',
    theme: 'ערכת נושא',
  },

  period: {
    label: 'תקופה',
    picker: 'בורר תקופה',
    currentMonth: (month: string) => `${month} (עד היום)`,
    comparedTo: 'לעומת',
    m3: '3 חודשים אחרונים',
    m12: '12 חודשים אחרונים',
    custom: 'טווח מותאם',
    customSoon: 'טווח מותאם יתווסף בגרסה המלאה',
    from: 'מתאריך',
    to: 'עד תאריך',
    tooLong: 'הטווח המרבי הוא 12 חודשים',
  },

  top: {
    refresh: 'רענן נתונים',
    refreshShort: 'רענן',
    refreshAria: 'רענן נתונים מ-Financy',
    updated: (rel: string) => `עודכן: ${rel}`,
    notifications: 'התראות',
    notificationsN: (n: number) => `התראות, ${n} חדשות`,
    userMenu: (name: string) => `תפריט משתמש — ${name}`,
    refreshMember: 'רק בעל החשבון יכול לרענן נתונים',
  },

  refresh: {
    title: 'לרענן נתונים מ-Financy?',
    body: 'הפעולה תעלה 20 קרדיטים מהחשבון. הנתונים יתעדכנו תוך 1–2 דקות.',
    bodyCredits: (used: string, plan: string) => `הפעולה תעלה 20 קרדיטים מהחשבון. נוצלו ${used} מתוך ${plan} החודש. הנתונים יתעדכנו תוך 1–2 דקות.`,
    confirm: 'רענן (20 קרדיטים)',
    ok: 'הנתונים עודכנו לפני רגע',
    failed: 'הרענון נכשל — Financy לא הגיב. נסה שוב בעוד דקה',
    retry: 'נסה שוב',
    noActive: 'אין חיבור פעיל ל-Financy. חבר מחדש באפליקציית Financy ואז רענן.',
    toConnections: 'להגדרות החיבורים',
  },

  freshness: {
    fresh: (rel: string) => `עודכן ${rel}`,
    warn: 'הנתונים לא עודכנו יותר מ-36 שעות',
    critical: (days: number) => `הנתונים לא עודכנו ${days} ימים`,
    none: 'אין נתונים מ-Financy',
    stale: (when: string) => `נתון ישן מ-${when}`,
    clickToRefresh: 'לחץ לרענון',
  },

  banner: {
    region: 'הודעות מערכת',
    expiredTitle: (provider: string) => `החיבור ל${provider} (עסקי) פג תוקף`,
    expiredBody: (day: string) => `— הנתונים מ-${day}`,
    openFinancy: 'פתח את אפליקציית Financy ← חבר מחדש',
    reconnect: 'חבר מחדש',
    whatIsThis: 'מה זה אומר?',
    whatIsThisBody: 'ההסכמה לבנק פגה. חידוש נעשה רק באפליקציית Financy; אחרי החידוש לחץ "רענן נתונים".',
    staleTitle: (when: string) => `הנתונים לא עודכנו מאז ${when}`,
    staleBody: '— יותר מ-36 שעות. רענן או בדוק את Financy.',
    checkConnection: 'בדוק את החיבור',
  },

  login: {
    title: 'כניסה',
    name: 'LabTop Finance',
    tagline: 'הכספים של LabTop: תזרים, גבייה ומע"מ במבט אחד',
    email: 'אימייל',
    password: 'סיסמה',
    emailRequired: 'נא להזין אימייל',
    passwordRequired: 'נא להזין סיסמה',
    showPassword: 'הצג סיסמה',
    hidePassword: 'הסתר סיסמה',
    remember: 'זכור אותי במכשיר הזה',
    submit: 'כניסה',
    forgot: 'שכחת סיסמה?',
    forgotToast: 'איפוס סיסמה יישלח למייל — בבנייה',
    passkey: 'כניסה ב-Passkey תתווסף בהמשך',
    demoHint: 'נתוני הדגמה · כל אימייל וסיסמה נכנסים',
    foot: '© LabTop · support@labtop.co.il',
    welcome: (name: string) => `ברוך הבא, ${name}`,
    failed: 'האימייל או הסיסמה שגויים. נסה שוב או שחזר סיסמה.',
  },

  home: {
    heroTitle: 'עו"ש עסקי · דיסקונט',
    heroLabel: 'יתרה זמינה',
    forecastTo: (day: string) => `צפי ל-${day}`,
    vsToday: 'לעומת היום',
    cardOutOfWindow: (day: string) => `הוצאות הכרטיס של החודש יורדות ב-${day} — מחוץ לחלון`,
    toForecast: 'לתחזית המלאה',
    noAccount: 'לא נמצא חשבון עסקי בחיבור Financy. ודא שהחיבור העסקי נוסף באפליקציית Financy.',
    dayOf: (day: number, total: number) => `יום ${day} מתוך ${total}`,
    income: 'הכנסות',
    incomeNote: 'לפני מע"מ, לפי חשבוניות',
    expenses: 'הוצאות',
    expensesNoteCurrent: 'מה שחויב בפועל עד היום',
    expensesNoteClosed: 'לפני מע"מ, 7 קטגוריות',
    profit: 'רווח תפעולי',
    profitNote: 'הכנסות − הוצאות',
    noPrevMonth: 'החודש הראשון בהיסטוריה — אין חודש קודם להשוואה',
    todo: 'דורש טיפול',
    todoSub: 'התראות פעילות',
    todoCount: (n: number) => `${n} התראות פעילות`,
    todoEmpty: 'אין מה לטפל כרגע. הכול במועד.',
    allAlerts: 'לכל ההתראות',
    moreAlerts: (n: number) => `עוד ${n} התראות`,
    collection: 'גבייה',
    collectionSub: 'חשבוניות פתוחות מ-HYP',
    open: 'פתוחות',
    ofWhichOverdue: (money: string, n: number) => `מזה באיחור ${money} (${n})`,
    topDebtors: '3 החייבים הגדולים',
    overdueCount: (n: number) => `(${n} באיחור)`,
    toOverdue: 'לחשבוניות באיחור',
    collectionEmpty: 'אין חשבוניות פתוחות. כל הכבוד.',
    hypDown: (when: string) => `HYP לא זמין כרגע — מוצגים נתונים מ-${when}`,
    tax: 'מע"מ ומסים',
    taxSub: (period: string, basis: string) => `תקופה ${period} · ${basis}`,
    taxSubPartial: (period: string, basis: string) => `תקופה ${period} · ${basis} · אומדן חלקי`,
    dueBy: (day: string, days: number) => `דיווח עד ${day} · נותרו ${days} ימים`,
    reportedPaid: (day: string) => `דווח ושולם ${day}`,
    outputVat: 'מע"מ עסקאות',
    inputVat: 'מע"מ תשומות',
    toPay: 'לתשלום (אומדן)',
    toPayPartial: 'לתשלום (אומדן חלקי)',
    paid: 'שולם',
    nonDeductible: (money: string) => `תשומות שלא יקוזזו: ${money}`,
    nonDeductibleCount: (n: number) => `${n} הוצאות ללא חשבונית`,
    unpaidVat: (n: number) => `מע"מ על ${n} חשבוניות שטרם שולמו`,
    unpaidVatNote: 'ידווח עם התשלום',
    nextDues: 'מועדי תשלום הבאים',
    totalBy: (day: string) => `סה"כ לתשלום עד ${day}`,
    noDues: 'אין מועדי תשלום קרובים.',
    toVat: 'למע"מ ומסים',
    ops: 'תפעול LabTop',
    opsTag: 'יתחבר ל-LabTop',
    openCalls: 'קריאות פתוחות',
    inProgress: (n: number) => `מזה ${n} בטיפול`,
    activeSubs: 'מנויים פעילים',
    mrr: 'MRR',
    avgClose: 'ממוצע זמן לסגירה',
    revenueByLine: (period: string) => `הכנסה ${period} לפי קו שירות · לפני מע"מ`,
    revenueByLineTotal: (money: string) => `סה"כ ${money} = הכנסות התקופה`,
    toOps: 'לתפעול',
    recent: 'תנועות אחרונות',
    recentSub: (mask: string) => `עו"ש + כרטיס ${mask} · 5 אחרונות`,
    recentSubNoCard: 'עו"ש · 5 אחרונות',
    allTx: 'לכל התנועות',
    incomeQuestion: 'מאיפה מגיעה ההכנסה?',
    incomeQuestionSub: '6 חודשים · לפני מע"מ · לפי קו שירות',
    balanceQuestion: 'לאן הולכת היתרה ב-30 יום?',
    noData: 'אין עדיין נתונים. אחרי המשיכה הראשונה מ-Financy המסך יתמלא.',
    noActiveConnections: 'אין חיבור פעיל ל-Financy. חבר מחדש באפליקציית Financy.',
  },

  card: {
    block: 'מחזור הכרטיס העסקי',
    blockSub: (mask: string) => `חיובי הכרטיס ${mask} יורדים מהעו"ש`,
    current: 'נצבר במחזור הנוכחי',
    next: 'לחיוב הקרוב',
    due: (day: string) => `יורד ב-${day}`,
    utilization: 'ניצול מסגרת',
    limit: (money: string) => `מתוך ${money}`,
    pending: (money: string) => `מזה ${money} ממתין`,
    charged: 'חויב',
    final: 'סופי',
    open: 'פתוח',
    status: 'סטטוס',
    enabled: 'פעיל',
    blocked: 'חסום',
    deleted: 'נסגר',
    none: 'לא נמצא כרטיס עסקי בחיבור Financy.',
  },

  cashflow: {
    title: 'נכנס מול יוצא — 12 חודשים',
    subCurrent: (range: string) => `${range} · תזרים עו"ש, ברוטו`,
    subClosed: (n: number, month: string) => `${n} חודשים עד ${month} · תזרים עו"ש, ברוטו`,
    fullMonths: (n: number, from: string, to: string) => `${n} חודשים מלאים (${from} – ${to})`,
    inLabel: 'נכנס',
    outLabel: 'יצא',
    netLabel: 'נטו',
    endBalance: 'יתרת סוף חודש',
    partialLabel: (day: string) => `ספט׳ (עד ${day})`,
    forecast: 'תחזית יתרת עו"ש',
    forecastSub: 'רק מה שמגיע לבנק בחלון, בתאריך שבו הוא מגיע',
    horizon: (days: number) => `${days} יום`,
    breakdown: (from: string, to: string) => `פירוט התחזית ל-30 יום (${from} עד ${to})`,
    forecastTotal: (day: string) => `= צפי ${day}`,
    balanceChart: 'יתרת עו"ש — בפועל ותחזית',
    balanceChartSub: (from: string, to: string, floor: string) =>
      `${from} עד ${to} · בפועל לפי יתרת סוף יום מהבנק · תחזית לפי אירועים במועדם · רף התראה ${floor}`,
    txTitle: 'תנועות בנק וכרטיס',
    txSubUnmatched: 'כל התקבולים שלא הותאמו (ללא תלות בחודש)',
    exportCsv: 'ייצוא CSV',
    filterAccount: 'חשבון',
    filterAccountAll: 'חשבון: הכול',
    filterAccountOne: 'חשבון שנבחר',
    filterChecking: 'עו"ש',
    filterCard: (mask: string) => `כרטיס ${mask}`,
    filterDir: 'כיוון',
    filterDirAll: 'כיוון: הכול',
    filterIn: 'נכנס',
    filterOut: 'יוצא',
    filterMatch: 'התאמה לחשבונית',
    filterMatchAll: 'התאמה: הכול',
    filterMatched: 'הותאם',
    filterUnmatched: 'לא הותאם',
    searchTx: 'חיפוש בתיאור',
    searchTxAria: 'חיפוש תנועות',
    emptyFilter: 'אין תנועות שתואמות לסינון.',
    emptyUnmatched: 'כל התקבולים הותאמו לחשבוניות.',
    emptyNoData: 'עדיין אין תנועות בחודש זה. אחרי חיבור החשבון העסקי ב-Financy, הנתונים יופיעו כאן.',
    matchAction: 'התאם לחשבונית',
    matchTitle: 'התאמת תקבול לחשבונית',
    matchBody: (money: string, day: string, desc: string) => `תקבול ${money} מ-${day} · ${desc}`,
    matchNone: 'ללא חשבונית (תקבול אחר)',
    matchEmpty: 'אין חשבונית פתוחה בסכום דומה.',
    matchOk: (number: string) => `התקבול הותאם ל-${number}`,
    matchOverdue: (n: number) => `באיחור ${n} ימים`,
    colDate: 'תאריך',
    colDesc: 'תיאור',
    colAccount: 'חשבון',
    colCategory: 'קטגוריה',
    colAmount: 'סכום',
    colVat: 'מע"מ',
    colInvoice: 'חשבונית',
    colMatch: 'התאמה',
    pending: 'ממתין',
    categoryChanged: 'הקטגוריה עודכנה',
    categoryMenu: 'בחר קטגוריה',
  },

  invoices: {
    open: 'פתוחות',
    openNote: (n: number) => `${n} חשבוניות · כולל מע"מ`,
    overdue: 'באיחור',
    overdueNote: (n: number) => `${n} חשבוניות`,
    oldest: (days: number) => `הוותיקה ${days} ימים`,
    collected: 'נגבה החודש',
    collectedNote: (range: string) => `${range} · תקבולים שהותאמו`,
    avgDays: 'ממוצע ימים לתשלום',
    avgDaysNote: '12 חודשים · לקוחות שוטף+30',
    agingQuestion: 'כמה מהחוב ישן?',
    agingSub: 'דליי גיל — ימים מעבר למועד התשלום · לחיצה מסננת',
    agingLegend: 'רמפה סדורה:',
    agingAria: 'דליי גיל חוב — לחיצה מסננת את החשבוניות',
    byCustomer: 'לפי לקוח',
    byCustomerSub: 'יתרה פתוחה · באיחור · ממוצע ימים לתשלום',
    colCustomer: 'לקוח',
    colOpenBalance: 'יתרה פתוחה',
    colOverdue: 'באיחור',
    colDaysToPay: 'ימים לתשלום',
    colMethod: 'אמצעי',
    colAction: 'פעולה',
    standingOrder: 'הו"ק',
    remind: 'תזכורת',
    list: 'רשימת חשבוניות',
    listSub: (shown: number) => `מ-HYP · ${shown} חשבוניות`,
    filterAll: 'הכול',
    filterOverdue: 'באיחור',
    filterOpen: 'פתוחות',
    filterPaid: 'שולמו',
    filterCredited: 'זוכו',
    filterCustomerAll: 'לקוח: הכול',
    filterRangeAll: 'טווח: הכול',
    filterRange: 'טווח תאריכים',
    searchInvoice: 'מס׳ חשבונית או לקוח',
    searchInvoiceAria: 'חיפוש חשבוניות',
    bucketFilter: (bucket: string) => `דלי ${bucket}`,
    clearBucket: 'נקה סינון דלי',
    colNumber: 'מס׳',
    colIssue: 'תאריך',
    colDue: 'מועד לתשלום',
    colNet: 'לפני מע"מ',
    colVat: 'מע"מ',
    colGross: 'כולל',
    colStatus: 'סטטוס',
    colReceipt: 'תקבול',
    immediate: 'מיידי',
    matched: (day: string) => `הותאם ${day}`,
    emptyFiltered: 'אין חשבוניות בטווח שנבחר.',
    emptyNone: 'עדיין אין חשבוניות מ-HYP. בדוק את חיבור HYP בהגדרות.',
    hypError: 'HYP לא הגיב (שגיאה 502).',
    hypErrorBody: (when: string) => `מוצגת תמונת מצב מ-${when}.`,
    swipeHint: 'החלקה שמאלה = סמן כשולם · החלקה ימינה = תזכורת · או פתיחה לפעולות',
    issuedOn: (day: string) => `הופקה ${day}`,
    dueOn: (day: string) => `לתשלום ${day}`,
    overdueDays: (n: number) => `באיחור ${n} ימים`,
    lateDays: (n: number) => `${n} ימים איחור`,
    paidOn: (day: string) => `שולם ${day}`,
    proformaNote: 'דרישת תשלום — לא נספרת למע"מ',
    issueDate: 'תאריך הפקה',
    dueDate: 'מועד לתשלום',
    paidAt: 'שולם ב-',
    customer: 'לקוח',
    terms: 'תנאי תשלום',
    termsNet30: 'שוטף+30',
    termsImmediate: 'מיידי',
    net: 'לפני מע"מ',
    vatRate: (rate: string) => `מע"מ ${rate}`,
    grossTotal: 'סה"כ כולל מע"מ',
    reminderHistory: 'היסטוריית תזכורות',
    reminderSent: (day: string) => `תזכורת נשלחה ${day}`,
    noReminders: 'לא נשלחו תזכורות',
    sendReminder: 'שלח תזכורת',
    markPaid: 'סמן כשולם',
    issueCredit: 'הוצא זיכוי',
    creditSoon: 'זיכוי מופק מ-HYP בגרסה המלאה',
    reminderTitle: (number: string) => `תזכורת לחשבונית ${number}`,
    reminderTo: 'נמענים',
    reminderSubject: 'נושא',
    reminderBody: 'גוף ההודעה',
    copyDraft: 'העתק טיוטה',
    openMail: 'פתח במייל',
    copied: 'הטיוטה הועתקה',
    copyFailed: 'ההעתקה נכשלה — סמן את הטקסט והעתק ידנית',
    markPaidTitle: (number: string) => `סימון ${number} כשולמה`,
    paidDate: 'תאריך תשלום',
    paidMethod: 'אמצעי',
    matchReceipt: 'התאם לתקבול בבנק',
    noReceipt: 'אין תקבול לא מותאם בסכום דומה',
    noReceiptOption: 'ללא התאמה לתקבול',
    markedPaid: (number: string) => `${number} סומנה כשולמה`,
    matchedToInvoice: (number: string) => `התקבול הותאם ל-${number}`,
    matchedInvoicesLabel: 'הותאם ל-',
    notMatched: 'לא הותאם',
    matchedShort: 'הותאם',
  },

  vat: {
    periodGroup: 'תקופה דו-חודשית',
    periodOpen: '(פתוחה)',
    export: 'ייצוא לרו"ח',
    exportCsv: 'CSV — חשבוניות ותשומות לתקופה',
    exportPdf: 'PDF — סיכום תקופה',
    basisCash: 'בסיס מזומן',
    basisAccrual: 'בסיס מצטבר',
    basisCashSub: 'בסיס מזומן — לפי תאריך התשלום',
    basisAccrualSub: 'בסיס מצטבר — לפי תאריך ההפקה',
    partialTitle: (range: string) => `תקופה פתוחה — אומדן חלקי (${range})`,
    outputVat: 'מע"מ עסקאות',
    outputCount: (n: number) => `(${n} חשבוניות מס-קבלה ששולמו בתקופה)`,
    outputCountPartial: (n: number, range: string) => `(${n} תשלומים שהתקבלו ${range})`,
    inputVat: 'מע"מ תשומות',
    inputCount: (n: number) => `(${n} הוצאות עם חשבונית)`,
    adjustments: 'רכב ודלק: קוזזו 2/3 · קבלנים (עוסק פטור): ללא מע"מ · רכישות מחו"ל: ללא מע"מ ישראלי',
    netVat: 'מע"מ לתשלום',
    netVatPartial: 'לתשלום (אומדן חלקי)',
    paid: 'שולם',
    notReported: 'טרם דווח',
    periodOpenChip: 'תקופה פתוחה',
    dueLine: (day: string, days: number) => `דיווח ותשלום עד ${day} · נותרו ${days} ימים`,
    dueLineShort: (day: string) => `דיווח ותשלום עד ${day}`,
    unpaidNote: (n: number, money: string) => `מע"מ על ${n} חשבוניות עסקה מהתקופה שטרם שולמו: ${money} — ידווח בתקופה שבה ישולמו`,
    markReported: 'סמן כדווח ושולם',
    markTitle: (label: string) => `סימון תקופת ${label} כמדווחת`,
    markDate: 'תאריך תשלום',
    markAmount: 'סכום ששולם',
    marked: 'התקופה סומנה כמדווחת',
    calendar: 'לוח מועדים',
    calendarSub: '60 הימים הקרובים · מע"מ, מקדמות, ביטוח לאומי',
    inDays: (n: number) => `בעוד ${n} ימים`,
    today: 'היום',
    byDate: (day: string) => `עד ${day}`,
    laterPrefix: 'הבא:',
    noDeadlines: 'אין מועדים ב-60 הימים הקרובים.',
    missing: 'הוצאות ללא חשבונית',
    missingSub: (period: string) => `${period} · ספקים ישראליים שמהם אפשר להשיג חשבונית מס`,
    missingCount: (n: number) => `${n} הוצאות`,
    missingSummary: (net: string, vat: string) => `${net} לפני מע"מ · ${vat} מע"מ שלא יקוזז`,
    hasInvoice: 'יש חשבונית',
    foreignNote: 'רכישות מחו"ל אינן ברשימה — אין בהן מע"מ ישראלי ואין מה לקזז.',
    missingEmpty: 'אין הוצאות ללא חשבונית — מצוין.',
    markedInvoice: (input: string, delta: string) => `סומן: יש חשבונית. התשומות עודכנו: ${input} (+${delta})`,
    trendQuestion: 'האם המע"מ לתשלום עולה?',
    trendSub: 'מע"מ עסקאות מול תשומות — 6 תקופות',
    history: 'היסטוריית דיווחים',
    historySub: 'תקופה · עסקאות · תשומות · לתשלום · סטטוס',
    colPeriod: 'תקופה',
    colOutput: 'עסקאות',
    colInput: 'תשומות',
    colNet: 'לתשלום',
    colPaidAt: 'שולם ב-',
    colStatus: 'סטטוס',
    partialChip: 'אומדן חלקי',
    notConfigured: 'הגדר סוג עוסק ואחוז מקדמות בהגדרות כדי לראות מועדים',
    toSettings: 'להגדרות המס',
    taxEventVat: 'מע"מ',
    taxEventAdvance: 'מקדמת מס הכנסה',
    taxEventNi: 'ביטוח לאומי',
  },

  expenses: {
    monthTitle: (period: string) => `הוצאות ${period}`,
    monthNote: 'לפני מע"מ · 7 קטגוריות תפעוליות',
    baselineProrated: (day: string) => `בסיס יחסי עד ${day}`,
    baselineFull: 'בסיס (ממוצע 3 חודשים)',
    above: 'מעל',
    below: 'מתחת',
    baselineNoteCurrent: (full: string, months: string, day: number, total: number) =>
      `בסיס חודשי מלא ${full} (ממוצע ${months}) × ${day}/${total}`,
    baselineNoteClosed: 'ממוצע 3 החודשים שקדמו',
    baselineNoneNote: 'אין חודשים קודמים לחישוב בסיס',
    missingTile: 'ללא חשבונית',
    missingTileCurrent: 'ללא חשבונית החודש',
    missingPrev: (month: string, n: number, money: string) => `ב${month}: ${n} (${money})`,
    missingFilterHint: 'לחיצה מסננת את הטבלה',
    overQuestion: 'מה חורג?',
    overSubCurrent: 'קטגוריות מול בסיס · חודש נוכחי — מול בסיס יחסי',
    overSubClosed: 'קטגוריות מול בסיס · מול בסיס מלא',
    barsAria: 'הוצאות לפי קטגוריה מול בסיס — לחיצה מסננת את הטבלה',
    baselineToDate: (money: string) => `בסיס עד כה ${money}`,
    baselineOf: (money: string) => `בסיס ${money}`,
    baselineFullMarker: (money: string) => `בסיס מלא ${money}`,
    barsLegend:
      'קו כהה = בסיס (ממוצע 3 חודשים; בחודש נוכחי — יחסי לימים שעברו). קו חיוור = בסיס חודשי מלא. שבב עם חץ למעלה = מעל סף ההתראה; העיפרון עורך את הסף.',
    editThreshold: (cat: string, current: string) => `ערוך סף התראה ל${cat} (${current})`,
    thresholdNone: 'ללא התראה',
    thresholdNow: (pct: number) => `כרגע ${pct}%`,
    thresholdSaved: (cat: string, pct: string) => `הסף ל${cat} עודכן ל-${pct}`,
    chartQuestion: 'על מה הולך הכסף לאורך זמן?',
    chartSub: 'הוצאות לפי קטגוריה — 6 חודשים · לפני מע"מ',
    otherFixed: 'קבועות אחרות',
    suppliers: 'ספקים חוזרים ומנויים',
    suppliersSub: 'סכום חודשי לפני מע"מ · הערוץ קובע מתי הסכום יורד מהעו"ש',
    colSupplier: 'ספק',
    colCategory: 'קטגוריה',
    colMonthly: 'חודשי',
    colChargeDay: 'יום חיוב',
    colNextCharge: 'חיוב הבא',
    colChannel: 'ערוץ',
    colInvoiceMode: 'חשבונית',
    invoiceAuto: 'אוטומטית',
    invoiceManual: 'ידני',
    foreignSupplier: (orig: string) => `ספק מחו"ל · מטבע מקורי ${orig} · ללא מע"מ`,
    fixedTotal: 'קבועות',
    fixedSplit: (card: string, bank: string) => `מזה בכרטיס ${card} · בבנק ${bank}`,
    tableTitle: (period: string) => `הוצאות ב${period}`,
    tableSub: (n: number) => `${n} הוצאות · לפני מע"מ, מע"מ, כולל`,
    filterCatAll: 'קטגוריה: הכול',
    filterInvAll: 'חשבונית: הכול',
    filterInvMissing: 'חסרה',
    filterInvForeign: 'מחו"ל',
    filterChannelAll: 'ערוץ: הכול',
    filterChannelCard: 'כרטיס',
    filterChannelBank: 'בנק',
    searchSupplier: 'חיפוש ספק',
    searchSupplierAria: 'חיפוש הוצאות',
    colDate: 'תאריך',
    colNet: 'לפני מע"מ',
    colVat: 'מע"מ',
    colGross: 'כולל',
    invoiceYes: 'יש',
    invoiceHas: 'יש חשבונית',
    invoiceMissing: 'חסרה',
    invoiceMissingLong: 'חסרה חשבונית',
    invoiceExempt: 'עוסק פטור',
    invoiceForeign: 'רכישה מחו"ל — ללא מע"מ ישראלי',
    invoiceForeignShort: 'מחו"ל',
    markHasInvoice: 'סמן שיש',
    markedHasInvoice: 'סומן: יש חשבונית. התשומות יעודכנו',
    grossOf: (money: string) => `כולל ${money}`,
    emptyMonth: 'אין הוצאות בחודש הזה.',
    emptyFilter: 'אין הוצאות שתואמות לסינון.',
    emptyMissing: 'אין הוצאות ללא חשבונית — מצוין.',
  },

  ops: {
    demoTag: 'יתחבר ל-LabTop · נתוני דמו במבנה LabTop',
    demoNote: 'קריאות, מנויים ולקוחות ייקראו מ-api.labtop.co.il כל 15 דק׳',
    openCalls: 'קריאות פתוחות',
    remoteField: (remote: number, field: number) => `${remote} מרחוק · ${field} שטח`,
    doneThisMonth: 'הושלמו החודש',
    avgClose: 'ממוצע זמן לסגירה',
    avgCloseNote: 'קריאות שנסגרו ב-30 הימים',
    sentinel: 'ממצאי סנטינל ← קריאות',
    sentinelNote: 'ניטור אוטומטי של עמדות המנויים',
    thisMonth: 'החודש',
    lineQuestion: 'איזה קו שירות צומח?',
    lineSub: 'הכנסה לפי קו שירות — 12 חודשים · לפני מע"מ',
    mrrQuestion: 'האם המנויים גדלים?',
    mrrSub: 'MRR ומנויים · 12 חודשים',
    mrr: 'MRR',
    active: 'פעילים',
    churn: 'נטישה 12 ח׳',
    subs: 'מנויים',
    subsSub: 'לקוח · תוכנית · עמדות · חודשי · אמצעי · חיוב הבא · סטטוס',
    colCustomer: 'לקוח',
    colPlan: 'תוכנית',
    colSeats: 'עמדות',
    colMonthly: 'חודשי',
    colBilling: 'אמצעי',
    colNextCharge: 'חיוב הבא',
    colStatus: 'סטטוס',
    billingStandingOrder: 'הו"ק HYP',
    billingInvoice: 'חשבונית שוטף+30',
    mrrRow: (n: number) => `MRR (${n} פעילים)`,
    seatsShort: (n: number) => `${n} עמדות`,
    top: 'לקוחות מובילים',
    topSub: (money: string) => `12 חודשים · חלק מההכנסה (${money})`,
    topAria: 'לקוחות מובילים לפי הכנסה',
    calls: 'קריאות אחרונות',
    callsSub: (n: number) => `${n} קריאות · מקור: לקוח או ממצא סנטינל`,
    callsFilterAria: 'סינון לפי סטטוס',
    filterAll: 'הכול',
    filterOpen: 'פתוחות',
    filterInProgress: 'בטיפול',
    filterDone: 'הושלמו',
    colNumber: 'מס׳',
    colType: 'סוג',
    colOpened: 'נפתחה',
    colTech: 'טכנאי',
    colPrice: 'מחיר',
    colSource: 'מקור',
    inPlan: 'במנוי',
    estimate: '(אומדן)',
    sourceCustomer: 'לקוח',
    sourceSentinel: 'סנטינל',
    noTech: 'ללא טכנאי',
    emptyCalls: 'אין קריאות בסטטוס הזה.',
    emptyAll: 'אין נתונים מ-LabTop. חבר את ה-API בהגדרות.',
  },

  settings: {
    title: 'הגדרות',
    sub: 'חיבורים · מס · התראות · תצוגה · חשבון',
    tabsAria: 'הגדרות',
    tabs: {
      connections: 'חיבורים',
      tax: 'מס',
      alerts: 'התראות',
      display: 'תצוגה',
      account: 'חשבון',
    },
    financy: 'Financy — בנק דיסקונט (עסקי)',
    updatedAt: 'עודכן',
    consentUntil: 'תוקף הסכמה',
    consentUntilValue: (day: string) => `עד ${day}`,
    accountRow: 'חשבון',
    cardRow: 'כרטיס',
    autoFetch: 'משיכה אוטומטית',
    autoFetchValue: (minutes: number) => `כל ${minutes} דקות`,
    refreshCredits: 'רענן (20 קרדיטים)',
    relink: 'חבר מחדש באפליקציית Financy',
    assign: 'שיוך חשבונות למערכת זו',
    assignNote:
      'שתי המערכות מושכות מאותו חשבון Financy ומסננות לפי חשבון/כרטיס. חיובי כרטיס פרטי שיורדים מהעו"ש העסקי יוצגו כ"חיוב כרטיס" ללא פירוט.',
    assignSwitch: (name: string) => `שייך למערכת זו: ${name}`,
    assignSaved: (name: string, on: boolean) => `${name} ${on ? 'שויך למערכת זו' : 'הוסר מהמערכת הזו'}`,
    assignOwnerOnly: 'רק בעל החשבון יכול לשנות שיוך חשבונות',
    hyp: 'HYP — סליקה וחשבוניות',
    hypTerminal: 'מזהה מסוף',
    hypKey: 'מפתח API',
    hypShowKey: 'הצג מפתח',
    hypTest: 'בדוק חיבור',
    hypNote: 'משיכת חשבוניות ותנועות כל שעה + webhook על תשלום',
    hypOk: (ms: number) => `החיבור ל-HYP תקין (${ms} ms)`,
    hypFail: 'HYP דחה את המפתח. בדוק במסוף HYP → API',
    labtop: 'LabTop API',
    labtopUrl: 'כתובת',
    labtopToken: 'טוקן',
    labtopTokenPlaceholder: 'יוזן כשהשרת יעלה',
    labtopNote: 'עד החיבור מוצגים נתוני דמו במבנה LabTop',
    labtopFail: 'השרת של LabTop עדיין לא עלה',
    connected: 'מחובר',
    notConnected: 'לא מחובר עדיין',
    secretsUnsupported: 'שמירת מפתחות מוצפנים תיפתח כשמודול ההצפנה יעלה — עד אז המפתח לא נשמר',
    businessKind: 'סוג עוסק',
    licensed: 'עוסק מורשה',
    exempt: 'עוסק פטור',
    lockedHere: 'נקבע בהתקנה',
    vatRate: 'שיעור מע"מ',
    vatFrequency: 'תדירות דיווח',
    bimonthly: 'דו-חודשי',
    monthly: 'חודשי',
    vatBasis: 'בסיס דיווח מע"מ',
    vatBasisNote: 'מזומן = מע"מ העסקאות נספר עם קבלת התשלום (ברירת מחדל לנותני שירותים)',
    advanceRate: 'אחוז מקדמות מס הכנסה',
    advanceRateNote: 'אחוז מהמחזור החודשי, לתשלום עד ה-15',
    ni: 'ביטוח לאומי חודשי',
    reportDay: 'יום דיווח',
    forecastGroup: 'תחזית תזרים',
    ownerDraw: 'משיכת בעלים חודשית',
    ownerDrawDay: 'ביום',
    floor: 'רף התראת תחזית',
    floorNote: 'התראה כשצפי היתרה ל-30 יום יורד מתחת לרף',
    saveTax: 'שמור הגדרות מס',
    taxSaved: 'הגדרות המס נשמרו — המועדים חושבו מחדש',
    alertsNote: 'ההתראות מוצגות בתוך המערכת בלבד (פעמון + מרכז התראות). ערוצים חיצוניים — לא בשלב זה.',
    alertRules: {
      INVOICE_OVERDUE: { label: 'חשבונית באיחור', hint: (r: AlertRuleValues) => `התראה ב-${r.invoiceOverdueDays} יום ואז כל ${r.invoiceOverdueRepeatDays} ימים` },
      VAT_DUE: { label: 'מועד מע"מ', hint: (r: AlertRuleValues) => `${r.vatWarnDays} ימים לפני, ${r.vatCriticalDays} ימים לפני, ביום` },
      TAX_ADVANCE_DUE: { label: 'מקדמות / ביטוח לאומי', hint: (r: AlertRuleValues) => `${r.taxAdvanceDays} ימים לפני` },
      CARD_CHARGE_SOON: { label: 'חיוב כרטיס עסקי', hint: (r: AlertRuleValues) => `${r.cardChargeDays} ימים לפני, עם בדיקת כיסוי` },
      CATEGORY_OVER_BASELINE: {
        label: 'חריגה מבסיס קטגוריה',
        hint: (r: AlertRuleValues) => `סף ${r.categoryThresholdPct}% · בחודש נוכחי מול בסיס יחסי החל מהיום ה-${r.categoryMinDayOfMonth}`,
      },
      RECEIPT_UNMATCHED: { label: 'תקבול לא מותאם', hint: (r: AlertRuleValues) => `אחרי ${r.receiptUnmatchedDays} ימים` },
      EXPENSE_NO_INVOICE: { label: 'הוצאה ללא חשבונית', hint: () => 'סיכום שבועי (יום א׳)' },
      FORECAST_NEGATIVE: { label: 'תחזית מתחת לרף', hint: (r: AlertRuleValues) => `יומי · רף ${r.floorLabel}` },
      DATA_STALE: { label: 'נתונים לא מעודכנים', hint: (r: AlertRuleValues) => `אחרי ${r.staleHours} שעות` },
      CONNECTION_EXPIRED: { label: 'חיבור פג תוקף', hint: () => 'מיידי, קבוע' },
      SUBSCRIPTION_CANCELLED: { label: 'ביטול מנוי', hint: () => 'מיידי' },
    } as Readonly<Record<AlertType, { label: string; hint: (r: AlertRuleValues) => string }>>,
    alertsSaved: 'הגדרות ההתראות נשמרו',
    theme: 'ערכת נושא',
    themeNote: '"אוטומטי" עוקב אחרי המערכת',
    themeLight: 'בהיר',
    themeDark: 'כהה',
    themeAuto: 'אוטומטי',
    density: 'צפיפות',
    densityNote: 'רגילה / דחוסה',
    densityNormal: 'רגילה',
    densityCompact: 'דחוסה',
    numberLocale: 'שפת מספרים',
    numberLocaleNote: 'מפרידי אלפים ומטבע',
    email: 'אימייל',
    changePassword: 'שנה סיסמה',
    changePasswordTitle: 'שינוי סיסמה',
    currentPassword: 'סיסמה נוכחית',
    newPassword: 'סיסמה חדשה',
    newPasswordHint: 'לפחות 10 תווים',
    passwordChanged: 'הסיסמה שונתה. יש להיכנס מחדש בשאר המכשירים.',
    devices: 'מכשירים מחוברים',
    logoutAll: 'יציאה מכל המכשירים',
    logoutAllDone: 'כל המכשירים נותקו — יש להיכנס מחדש',
    exportAll: 'ייצוא כל הנתונים',
    exportAllNote: 'JSON — תנועות, חשבוניות, הגדרות',
    exportAllBtn: 'ייצוא (JSON)',
    logout: 'התנתק',
    logoutNote: 'מהמכשיר הזה בלבד',
    saved: 'ההגדרות נשמרו',
  },

  alerts: {
    title: 'מרכז התראות',
    sub: (unread: number, open: number) => `${unread} לא נקראו · ${open} פעילות`,
    readAll: 'סמן הכול כנקרא',
    filterAll: 'הכול',
    filterUnread: 'לא נקראו',
    filterCritical: 'קריטי',
    filterOverdue: 'באיחור',
    filterTax: 'מס',
    filterOps: 'תפעול',
    unreadDot: 'לא נקראה',
    dismiss: 'בטל התראה',
    dismissed: 'ההתראה בוטלה',
    undo: 'בטל',
    handled: 'טופל',
    empty: 'אין התראות. נעדכן כשמשהו ידרוש טיפול.',
    unreadCount: (n: number) => `${n} התראות חדשות`,
    noneNew: 'אין התראות חדשות',
    today: 'היום',
    yesterday: 'אתמול',
    thisWeek: 'השבוע',
    earlier: 'קודם',
    panelTitle: 'התראות',
    panelAll: 'לכל ההתראות',
  },

  errors: {
    UNAUTHENTICATED: 'הכניסה פגה. יש להיכנס מחדש.',
    INVALID_CREDENTIALS: 'האימייל או הסיסמה שגויים. נסה שוב או שחזר סיסמה.',
    FORBIDDEN: 'אין לך הרשאה לפעולה הזו.',
    CSRF: 'הבקשה נדחתה מטעמי אבטחה. רענן את הדף ונסה שוב.',
    NOT_FOUND: 'הפריט לא נמצא.',
    METHOD_NOT_ALLOWED: 'הפעולה אינה נתמכת.',
    VALIDATION: 'אחד הערכים אינו תקין.',
    INVALID_PERIOD: 'התקופה שנבחרה אינה תקינה.',
    PERIOD_TOO_LONG: 'הטווח המרבי הוא 12 חודשים.',
    INVALID_CURSOR: 'הדף המבוקש אינו תקין. נסה לטעון מחדש.',
    WEAK_PASSWORD: 'הסיסמה קצרה מדי — לפחות 10 תווים.',
    INVALID_VAT_PERIOD: 'תקופת המע"מ אינה קיימת.',
    EMAIL_TAKEN: 'האימייל כבר בשימוש.',
    USER_LIMIT: 'הגעת למספר המשתמשים המרבי.',
    LAST_OWNER: 'לא ניתן להסיר את בעל החשבון היחיד.',
    CONFLICT: 'הנתונים השתנו בינתיים. רענן ונסה שוב.',
    VAT_PERIOD_LOCKED: 'התקופה כבר דווחה — אי אפשר לשנות אותה.',
    ALREADY_MATCHED: 'התקבול כבר הותאם לחשבונית אחרת.',
    AMOUNT_MISMATCH: 'הסכומים אינם תואמים. אשר התאמה ידנית כדי להמשיך.',
    INVOICE_NOT_PAYABLE: 'אי אפשר לסמן את החשבונית הזו כשולמה.',
    REFRESH_COOLDOWN: 'הנתונים נמשכו לאחרונה. נסה שוב עוד כמה דקות.',
    SYNC_IN_PROGRESS: 'משיכה כבר רצה כרגע.',
    NO_ACTIVE_CONNECTIONS: 'אין חיבור פעיל ל-Financy.',
    PAYLOAD_TOO_LARGE: 'הבקשה גדולה מדי.',
    RATE_LIMITED: 'יותר מדי ניסיונות. נסה שוב בעוד דקה.',
    NOT_IMPLEMENTED: 'הפעולה תתווסף בגרסה המלאה.',
    FINANCY_UNAVAILABLE: 'Financy לא הגיב. נסה שוב בעוד דקה.',
    HYP_UNAVAILABLE: 'HYP לא זמין כרגע. מוצגת תמונת המצב האחרונה.',
    HYP_NOT_CONFIGURED: 'HYP לא מוגדר. הזן מזהה מסוף ומפתח API בהגדרות.',
    HYP_REJECTED: 'HYP דחה את המפתח. בדוק במסוף HYP → API.',
    LABTOP_UNAVAILABLE: 'שרת LabTop לא זמין כרגע.',
    LABTOP_NOT_CONFIGURED: 'LabTop API לא מוגדר. הזן כתובת וטוקן בהגדרות.',
    INTERNAL: 'משהו השתבש. נסה שוב.',
    NETWORK: 'אין חיבור לשרת. בדוק את הרשת ונסה שוב.',
  } as Readonly<Record<ErrorCode | 'NETWORK', string>>,


  /** REDESIGN §3 — the written summary at the top of the home screen. */
  narrative: {
    title: 'מה קורה',
    region: 'סיכום מילולי',
  },

  /** REDESIGN §2/§4 — the time buckets and the one row component they share. */
  urgency: {
    region: 'מה דורש טיפול, לפי דחיפות',
    late: 'באיחור',
    action: 'לפרטים',
    more: (n: number) => `עוד ${n}`,
    less: 'הצג פחות',
    laterToggle: (n: number) => (n === 1 ? 'דבר אחד שאפשר לדחות' : `${n} דברים שאפשר לדחות`),
    show: 'הצג',
    hide: 'הסתר',
    emptyTitle: 'אין מה לטפל עכשיו',
    emptyBody: 'אין דיווח, תשלום או חשבונית שדורשים פעולה בימים הקרובים.',
    vatReport: (period: string) => `דיווח ותשלום מע"מ ${period}`,
    invoicesOverdue: (n: number) => (n === 1 ? 'חשבונית אחת באיחור בגבייה' : `${n} חשבוניות באיחור בגבייה`),
    noInvoice: (n: number) => (n === 1 ? 'הוצאה אחת ללא חשבונית מס' : `${n} הוצאות ללא חשבונית מס`),
    forecastNegative: 'לפי הצפי היתרה יורדת מתחת לאפס',
    connectionExpired: (provider: string) => `החיבור ל${provider} פג תוקף`,
    clientFallback: 'השרת לא שלח את הסיכום והדחיפויות — המסך מציג שחזור מקומי, לא את מה שרץ בשרת.',
    dataStale: 'הנתונים לא עודכנו מ-Financy',
  },

  /** REDESIGN §4.6/§5 — the quiet detail strip below the fold. */
  strip: {
    title: 'פירוט',
    cashflow: 'תזרים החודש',
    cashflowLink: 'לתזרים',
    collection: 'גבייה',
    collectionLink: 'לחשבוניות',
    tax: 'מע"מ ומסים',
    taxLink: 'למע"מ',
    expenses: 'הוצאות',
    expensesLink: 'להוצאות',
    ops: 'תפעול LabTop',
    opsLink: 'לתפעול',
    loans: 'הלוואות עסקיות',
    loansLink: 'להלוואות',
    recentLink: 'לכל התנועות',
    noLoans: 'אין הלוואות עסקיות פתוחות',
  },

  /** REDESIGN §6 — the loans screen. */
  loans: {
    title: 'הלוואות',
    sub: (n: number, scope: string) => `${n} הלוואות · ${scope}`,
    subEmpty: 'אין הלוואות',
    totalDebt: 'סך החוב',
    totalDebtNote: (n: number) => (n === 1 ? 'הלוואה פתוחה אחת' : `${n} הלוואות פתוחות`),
    monthly: 'תשלום חודשי',
    monthlyNote: 'סך התשלומים החודשיים',
    weightedRate: 'ריבית משוקללת',
    weightedRateNote: 'ממוצע משוקלל ביתרות',
    yearInterest: 'הריבית שתשלם השנה',
    yearInterestNote: '12 החודשים הבאים, לפי היתרה והריבית הנוכחיות',
    estimated: 'מוערך',
    estimatedTitle: 'הספק לא מוסר תשלום חודשי — הסכום מחושב מהיתרה, מהריבית ומהתקופה שנותרה',
    trend: 'החוב לאורך זמן',
    trendProjected: 'תחזית לפי לוח הסילוקין',
    trendActual: '12 חודשים אחרונים',
    trendFalling: (money: string) => `יורד ב-${money} בחודש בקצב הנוכחי`,
    trendRising: (money: string) => `עולה ב-${money} בחודש בקצב הנוכחי`,
    trendFlat: 'החוב לא משתנה בקצב הנוכחי',
    trendAria: 'סך החוב לאורך זמן',
    ranking: 'הכי יקר קודם',
    // §6 keeps card credit out of the bank list, so no single list is a global ranking: the order
    // is descending WITHIN each group, and the expensive group leads. The sub-line says that.
    rankingSub: 'בכל קבוצה מהריבית הגבוהה לנמוכה, והקבוצה היקרה קודם',
    topRate: 'היקרה ביותר',
    topRateOf: (name: string, rate: string) => `${name} — ${rate}`,
    more: (n: number) => (n === 1 ? 'עוד הלוואה אחת' : `עוד ${n} הלוואות`),
    sort: 'מיון',
    sortRate: 'ריבית',
    sortBalance: 'יתרה',
    sortPayment: 'תשלום חודשי',
    sortEnd: 'תאריך סיום',
    scope: 'שיוך',
    scopeAll: 'הכל',
    scopeBusiness: 'עסקי',
    scopePersonal: 'פרטי',
    groupBank: 'הלוואות בנק',
    groupCard: 'קרדיט בכרטיס',
    colRate: 'ריבית',
    colBalance: 'יתרה',
    colPayment: 'חודשי',
    colEnd: 'סיום',
    yearInterestRow: (money: string) => `ריבית שנתית ≈ ${money}`,
    expand: 'הצג את מבנה הריבית',
    collapse: 'הסתר את מבנה הריבית',
    interest: 'מבנה הריבית',
    interestFixed: 'ריבית קבועה',
    interestIndexed: 'ריבית משתנה',
    interestIndex: (index: string) => `צמודה ל${index}`,
    interestTier: (from: string, to: string, rate: string) => `${from}–${to}: ${rate}`,
    interestUnknown: 'הספק לא מוסר את מבנה הריבית',
    contract: 'תקופת ההלוואה',
    contractRange: (from: string, to: string) => `${from} – ${to}`,
    contractOpen: 'ללא תאריך סיום',
    monthsLeft: (n: number) => (n === 1 ? 'נותר חודש אחד' : n === 2 ? 'נותרו חודשיים' : `נותרו ${n} חודשים`),
    toTransactions: 'לתנועות של החשבון',
    timeline: 'מה נגמר ומתי',
    timelineAria: 'ציר הזמן של סיום ההלוואות',
    freeingUp: (months: number, count: number, money: string) =>
      `בעוד ${months === 1 ? 'חודש' : months === 2 ? 'חודשיים' : `${months} חודשים`} ${
        count === 1 ? 'תיגמר הלוואה אחת ויתפנו' : `ייגמרו ${count} הלוואות ויתפנו`
      } ${money} בחודש.`,
    freeingUpNone: 'אין הלוואה שנגמרת בחמש השנים הקרובות.',
    timelineUndated: (n: number) => (n === 1 ? 'הלוואה אחת ללא תאריך סיום' : `${n} הלוואות ללא תאריך סיום`),
    closed: (n: number) => (n === 1 ? 'הלוואה אחת שנסגרה' : `${n} הלוואות שנסגרו`),
    closedShow: 'הצג',
    closedHide: 'הסתר',
    emptyTitle: 'אין הלוואות בשיוך הזה',
    emptyBody: 'החליפו את השיוך למעלה, או בדקו את שיוך החשבונות בהגדרות.',
    emptyAction: 'לשיוך חשבונות',
    noneTitle: 'אין הלוואות',
    noneBody: 'לא נמצאו חשבונות הלוואה בחיבורי Financy של העסק.',
    derivedNote: 'התשלום החודשי והתחזית מחושבים מהיתרה, מהריבית ומהתקופה שנותרה.',
  },

  /** REDESIGN §7d — every list that can be empty says what it means, never "אין נתונים". */
  empty: {
    noAlertsTitle: 'אין התראות פתוחות',
    noAlertsBody: 'כשמשהו ידרוש טיפול — דיווח, גבייה או חריגה — הוא יופיע כאן.',
    noInvoicesTitle: 'אין חשבוניות פתוחות',
    noInvoicesBody: 'כל מה שהופק ב-HYP נגבה. חשבונית חדשה תופיע כאן אחרי ההפקה.',
    noResultsTitle: 'אין תוצאות לסינון הזה',
    noResultsBody: 'נסו טווח תאריכים אחר, קטגוריה אחרת או חיפוש קצר יותר.',
    noAnomaliesTitle: 'אין חריגות החודש',
    noAnomaliesBody: 'ההוצאות בכל הקטגוריות בתוך הטווח הרגיל שלהן.',
    reconnectTitle: 'החיבור ל-Financy פג תוקף',
    reconnectBody: 'חידוש ההסכמה נעשה באפליקציית Financy; אחרי החידוש לחצו "רענן נתונים".',
    firstRunTitle: 'עדיין אין נתונים',
    firstRunBody: 'אחרי המשיכה הראשונה מ-Financy המסך יתמלא מעצמו.',
    noHistoryTitle: 'אין עדיין היסטוריה',
    noHistoryBody: 'הגרף יתמלא ככל שיצטברו נתונים.',
  },

  screenError: {
    title: 'המסך לא נטען.',
    body: (title: string) => `אירעה שגיאה בהצגת "${title}". נסה לבחור תקופה אחרת או לחזור למסך הראשי.`,
    home: 'למסך הראשי',
    retry: 'נסה שוב',
  },
} as const;

/** The values the alert-rule hints interpolate (settings → התראות). */
export interface AlertRuleValues {
  invoiceOverdueDays: number;
  invoiceOverdueRepeatDays: number;
  vatWarnDays: number;
  vatCriticalDays: number;
  taxAdvanceDays: number;
  cardChargeDays: number;
  categoryThresholdPct: number;
  categoryMinDayOfMonth: number;
  receiptUnmatchedDays: number;
  staleHours: number;
  floorLabel: string;
}
